import { actionability } from './linkFacts';
import { siteKey } from './siteGraph';
import type { LinkEdge, LinkMapPage, SiteNode } from '../../types';

/**
 * Picking two sites and deciding what a drop between their pages means.
 *
 * Named `sitePairing` rather than `sitePageDock` so it does not collide with
 * `SitePageDock.tsx` on a case-insensitive filesystem — TypeScript resolves the
 * two to one file and reports a module with no default export, which reads as
 * anything but a filename problem. Same split as `linkFacts` / `LinkTree`.
 *
 * Dependency-free, like `linkFacts` and `siteGraph`, and for the same
 * reason: the component imports d3, which CRA's jest cannot load, so anything
 * that needs a test has to live outside it.
 *
 * The rules here are all quiet when wrong. A drop that should have been refused
 * gets refused by the server instead, as a 409 the user reads as a bug. A page
 * shown in the wrong column links the wrong site. A missing already-linked
 * marker turns a work surface back into a picker.
 */

/** Null is the "no site yet" bucket — a real site here, not missing data. */
export type SiteId = number | null;

/**
 * Which two sites the dock is showing, left to right.
 *
 * A SHIFT REGISTER, not two slots you assign to. Clicking a site puts it on the
 * right and pushes the previous right-hand one left, so clicking two sites in
 * order gives exactly those two in that order. The alternative — "fills the
 * first empty column, then replaces… one of them" — needs a rule the user has
 * to learn and cannot see.
 */
export interface DockPair {
  left: SiteId | undefined;
  right: SiteId | undefined;
}

export const EMPTY_PAIR: DockPair = { left: undefined, right: undefined };

export function selectSite(pair: DockPair, site: SiteId): DockPair {
  // Re-clicking the site already on the right is a no-op rather than a shift
  // that duplicates it. Clicking a DIFFERENT one when it is already on the
  // left, though, is how you deliberately get the same site twice.
  if (pair.right !== undefined && sameSite(pair.right, site)) return pair;
  return { left: pair.right, right: site };
}

/** Both columns showing one site, for linking WITHIN it. Not a degenerate case
 *  — self-links are the majority of links on most maps. */
export function selectBoth(site: SiteId): DockPair {
  return { left: site, right: site };
}

export function closeColumn(pair: DockPair, side: 'left' | 'right'): DockPair {
  return side === 'left'
    ? { left: undefined, right: pair.right }
    : { left: pair.left, right: undefined };
}

/**
 * Which column a site occupies, if any.
 *
 * `both` is a real answer, not an edge case: the same site in both columns is
 * how you link within one site, which is most of the links on a real map. A
 * caller that only asked "is it selected" could not draw that differently, and
 * two columns of the same pages with nothing saying so is confusing.
 */
export function columnOf(
  pair: DockPair,
  site: SiteId,
): 'left' | 'right' | 'both' | null {
  const isLeft = pair.left !== undefined && sameSite(pair.left, site);
  const isRight = pair.right !== undefined && sameSite(pair.right, site);
  if (isLeft && isRight) return 'both';
  if (isLeft) return 'left';
  if (isRight) return 'right';
  return null;
}

/**
 * Let go of any column whose site is no longer in the map.
 *
 * Removing a source takes its pages with it, so a column left pointing at that
 * site renders an empty list under a live header — and its node is gone from
 * the graph above, so there is no longer anything to close it from.
 */
export function prunePair(pair: DockPair, live: SiteId[]): DockPair {
  const keys = new Set(live.map(siteKey));
  const keep = (site: SiteId | undefined) =>
    site !== undefined && keys.has(siteKey(site)) ? site : undefined;
  return { left: keep(pair.left), right: keep(pair.right) };
}

/**
 * The two sites to open with.
 *
 * The dock used to start empty, which made the first thing you saw a graph with
 * a hint telling you to click something. The pages are the point of this lens,
 * so it opens showing some.
 *
 * WHICH two, in order of what is most likely to be worth looking at:
 *   - the busiest existing flow, because a pair that already links is the pair
 *     you are most likely to be adding to;
 *   - failing that, the two busiest sites, `sites` being sorted busiest-first
 *     by the server;
 *   - and a one-site map opens that site on BOTH sides, because linking within
 *     it is the only thing there is to do.
 */
export function initialPair(
  sites: SiteNode[],
  flows: { from_site_id: SiteId; to_site_id: SiteId; count: number }[],
): DockPair {
  if (sites.length === 0) return EMPTY_PAIR;
  if (sites.length === 1) return selectBoth(sites[0].site_id);

  const known = new Set(sites.map((s) => siteKey(s.site_id)));
  const busiest = flows
    .filter(
      (f) =>
        known.has(siteKey(f.from_site_id)) && known.has(siteKey(f.to_site_id)),
    )
    .reduce<(typeof flows)[number] | null>(
      (best, f) => (best === null || f.count > best.count ? f : best),
      null,
    );
  if (busiest) {
    return busiest.from_site_id === busiest.to_site_id
      ? selectBoth(busiest.from_site_id)
      : { left: busiest.from_site_id, right: busiest.to_site_id };
  }
  return { left: sites[0].site_id, right: sites[1].site_id };
}

export function isPairEmpty(pair: DockPair): boolean {
  return pair.left === undefined && pair.right === undefined;
}

function sameSite(a: SiteId, b: SiteId): boolean {
  return siteKey(a) === siteKey(b);
}

/**
 * One site's pages, in the order that makes the column usable.
 *
 * Sorted by `actionability` — the Tree's own ordering — so the pages you can
 * act on are at the top. Against real data the first actionable page sat at row
 * 13 of 59; a column that repeats that is a column you scroll past.
 */
export function pagesForSite(pages: LinkMapPage[], site: SiteId): LinkMapPage[] {
  return pages
    .filter((p) => sameSite(p.site_id, site))
    .sort(
      (a, b) =>
        actionability(a) - actionability(b) ||
        a.title.localeCompare(b.title) ||
        a.id - b.id,
    );
}

/**
 * Narrow a column to what matches a search.
 *
 * The SAME HAYSTACK the tree searches — title, target keyword and subject path
 * — because the two surfaces list the same pages and a query that finds a page
 * in one and not the other reads as missing data. `filterRows` cannot be reused
 * directly: it works on assembled tree rows, and these are plain pages.
 *
 * Whitespace-separated terms, all of which must match, so "trek grips" narrows
 * rather than widening the way a single substring would.
 */
export function filterPages(pages: LinkMapPage[], query: string): LinkMapPage[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return pages;
  return pages.filter((p) => {
    const hay = `${p.title} ${p.keyword ?? ''} ${p.subject_path ?? ''}`.toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

export interface SegmentFocus {
  /** Pages the links leave FROM — the left column. */
  sources: Set<number>;
  /** Pages they arrive AT — the right column. */
  targets: Set<number>;
}

/**
 * The two ends of one arrow's links, split by which column each belongs in.
 *
 * Clicking an arrow's count already loaded both its sites, but the columns
 * still listed every page on them, so "which twelve of these 61 pages are the
 * twelve" was a question the screen would not answer. This narrows to exactly
 * the pages involved.
 *
 * Split rather than one set, because a page can be a source of one link and the
 * target of another, and a single set would show it on both sides — implying a
 * link that is not in this segment.
 */
export function segmentFocus(
  edges: { page_id: number; linked_page_id: number }[],
): SegmentFocus {
  return {
    sources: new Set(edges.map((e) => e.page_id)),
    targets: new Set(edges.map((e) => e.linked_page_id)),
  };
}

/**
 * Page ids that already link to something in the other column, either way
 * round.
 *
 * BOTH DIRECTIONS on purpose. The question a marker answers is "have I already
 * dealt with this page", and a page that is linked FROM the other side has been
 * dealt with just as much as one linking TO it. Direction is what the arrow in
 * the graph is for.
 */
export function linkedAcross(
  edges: LinkEdge[],
  mine: LinkMapPage[],
  theirs: LinkMapPage[],
): Set<number> {
  const here = new Set(mine.map((p) => p.id));
  const there = new Set(theirs.map((p) => p.id));
  const out = new Set<number>();
  for (const e of edges) {
    if (here.has(e.page_id) && there.has(e.linked_page_id)) out.add(e.page_id);
    if (here.has(e.linked_page_id) && there.has(e.page_id)) out.add(e.linked_page_id);
  }
  return out;
}

export type DropVerdict =
  | { ok: true }
  | { ok: false; reason: string };

/**
 * Whether this drop should be attempted at all.
 *
 * `dropped` is the page being dropped ON — the one that GAINS the link — and
 * `dragged` is what it will point at. That is the Tree's rule, kept identical
 * here so one gesture does not mean two things in one app.
 *
 * The duplicate check exists because the server's `unique_together` on
 * (link_map, page, linked_page) answers a repeat drop with a 409, which
 * surfaces as a red error for a gesture that did nothing wrong.
 */
export function dropVerdict(
  dragged: LinkMapPage | undefined,
  dropped: LinkMapPage | undefined,
  edges: LinkEdge[],
): DropVerdict {
  if (!dragged || !dropped) return { ok: false, reason: 'That page is not in this map.' };
  if (dragged.id === dropped.id) {
    return { ok: false, reason: 'A page cannot link to itself.' };
  }
  const exists = edges.some(
    (e) => e.page_id === dropped.id && e.linked_page_id === dragged.id,
  );
  if (exists) {
    return {
      ok: false,
      reason: `“${dropped.title}” already links to “${dragged.title}”.`,
    };
  }
  return { ok: true };
}

/** What the drop just did, in the direction it actually happened. Said out loud
 *  because the rule is genuinely surprising: you drop ON the page that gains
 *  the link. */
export function linkNotice(
  dragged: LinkMapPage,
  dropped: LinkMapPage,
  bidirectional: boolean,
): string {
  return bidirectional
    ? `“${dropped.title}” and “${dragged.title}” now link to each other.`
    : `“${dropped.title}” now links to “${dragged.title}”.`;
}

/** The MIME type the drag carries. A custom type rather than `text/plain` so a
 *  page cannot be dropped into a text field, or arbitrary text into a column. */
export const PAGE_DRAG_TYPE = 'application/x-jcseo-page';
